import { useFeatureGate } from '@statsig/expo-bindings';
import * as FileSystem from 'expo-file-system';
import { useState } from 'react';
import { isWeb } from 'smoothie';

import { formattedDollarsToCents } from '../../../common/utils/bankingUtils';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import { captureException } from '../../../logger/Logger';
import useCreateRcdDeposit from '../../hooks/useCreateRcdDeposit';
import type {
    CheckDepositFormData,
    CheckImage,
    CheckSide,
} from '../../types/CheckDeposit';
import CheckDepositCamera from './CheckDepositCamera';
import CheckDepositDetails from './CheckDepositDetails';
import { CheckDepositEndorsementSheet } from './CheckDepositEndorsementSheet';
import CheckDepositFlip from './CheckDepositFlip';
import CheckDepositReview from './CheckDepositReview';
import CheckDepositSuccess from './CheckDepositSuccess';

type Step = 'details' | 'camera' | 'flip' | 'review' | 'success';

type DepositAccount = NonNullable<CheckDepositFormData['depositAccount']>;

const arrayBufferToBase64 = (buffer: ArrayBuffer): string =>
    btoa(
        Array.from(new Uint8Array(buffer), (b) => String.fromCharCode(b)).join(
            '',
        ),
    );

const readImageAsBase64 = async (uri: string): Promise<string> => {
    if (!isWeb) {
        return await FileSystem.readAsStringAsync(uri, {
            encoding: FileSystem.EncodingType.Base64,
        });
    }

    const response = await fetch(uri);

    if (!response.ok) {
        throw new Error(
            `Unable to fetch image for base64 encoding: ${response.status}`,
        );
    }

    const imageArrayBuffer = await response.arrayBuffer();
    return arrayBufferToBase64(imageArrayBuffer);
};

const CheckDeposit = () => {
    const { value: enableMobileCheckDeposit } = useFeatureGate(
        FeatureFlags.ENABLE_MOBILE_CHECK_DEPOSIT,
    );

    const [step, setStep] = useState<Step>('details');
    const [amountCents, setAmountCents] = useState<number | null>(null);
    const [depositAccount, setDepositAccount] = useState<DepositAccount | null>(
        null,
    );
    const [frontImage, setFrontImage] = useState<CheckImage | null>(null);
    const [backImage, setBackImage] = useState<CheckImage | null>(null);
    const [cameraSide, setCameraSide] = useState<CheckSide>('front');
    const [cameraReturnStep, setCameraReturnStep] = useState<
        'details' | 'review'
    >('details');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [depositError, setDepositError] = useState<string | null>(null);
    const [hasConfirmedEndorsement, setHasConfirmedEndorsement] =
        useState(false);
    const [isEndorsementSheetOpen, setIsEndorsementSheetOpen] = useState(false);
    const [pendingDetails, setPendingDetails] =
        useState<CheckDepositFormData | null>(null);

    const { mutateAsync: submitDeposit } = useCreateRcdDeposit();
    const endorsementAccountNumberMasked =
        pendingDetails?.depositAccount?.accountNumberMasked ?? '';

    const openCamera = (side: CheckSide, returnTo: 'details' | 'review') => {
        setCameraSide(side);
        setCameraReturnStep(returnTo);
        setStep('camera');
    };

    const proceedFromDetails = (data: CheckDepositFormData) => {
        setAmountCents(formattedDollarsToCents(data.amount));
        setDepositAccount(data.depositAccount);
        openCamera('front', 'details');
    };

    const handleDetailsSubmit = (data: CheckDepositFormData) => {
        if (!hasConfirmedEndorsement) {
            setPendingDetails(data);
            setIsEndorsementSheetOpen(true);
            return;
        }
        proceedFromDetails(data);
    };

    const handleEndorsementContinue = () => {
        if (pendingDetails == null) return;
        setHasConfirmedEndorsement(true);
        setIsEndorsementSheetOpen(false);
        proceedFromDetails(pendingDetails);
        setPendingDetails(null);
    };

    const handleCapture = (image: CheckImage) => {
        if (cameraSide === 'front') {
            setFrontImage(image);
            if (backImage) {
                setStep('review');
            } else {
                setCameraSide('back');
                setStep('flip');
            }
        } else {
            setBackImage(image);
            setStep('review');
        }
    };

    const handleConfirm = async () => {
        if (!frontImage || !backImage || !depositAccount || amountCents == null)
            return;
        setDepositError(null);
        setIsSubmitting(true);
        try {
            const [frontBase64, backBase64] = await Promise.all([
                readImageAsBase64(frontImage.uri),
                readImageAsBase64(backImage.uri),
            ]);

            const result = await submitDeposit({
                body: {
                    accountId: depositAccount.id,
                    checkAmountCents: amountCents,
                    frontImage: frontBase64,
                    backImage: backBase64,
                },
            });

            if (result.success) {
                setStep('success');
            } else {
                setDepositError(result.userMessage);
            }
        } catch (e) {
            captureException(e);
            setDepositError(
                'Something went wrong. Please try again or contact support@fruitful.com.',
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!enableMobileCheckDeposit) return null;

    const renderStep = () => {
        if (amountCents == null || !depositAccount || step === 'details') {
            return <CheckDepositDetails onSubmit={handleDetailsSubmit} />;
        }

        switch (step) {
            case 'flip':
                return (
                    <CheckDepositFlip
                        onDone={() => {
                            setStep('camera');
                        }}
                    />
                );
            case 'camera':
                return (
                    <CheckDepositCamera
                        side={cameraSide}
                        onCapture={handleCapture}
                        onCancel={() => {
                            setStep(cameraReturnStep);
                        }}
                    />
                );
            case 'success':
                return (
                    <CheckDepositSuccess
                        amountCents={amountCents}
                        accountNumberMasked={depositAccount.accountNumberMasked}
                    />
                );
            case 'review':
            default:
                if (!frontImage || !backImage) {
                    return (
                        <CheckDepositDetails onSubmit={handleDetailsSubmit} />
                    );
                }
                return (
                    <CheckDepositReview
                        amountCents={amountCents}
                        depositAccount={depositAccount}
                        frontImage={frontImage}
                        backImage={backImage}
                        isSubmitting={isSubmitting}
                        errorMessage={depositError}
                        onBack={() => {
                            setDepositError(null);
                            setStep('details');
                        }}
                        onRetakeFront={() => {
                            setDepositError(null);
                            openCamera('front', 'review');
                        }}
                        onRetakeBack={() => {
                            setDepositError(null);
                            openCamera('back', 'review');
                        }}
                        onConfirm={handleConfirm}
                    />
                );
        }
    };

    return (
        <>
            {renderStep()}
            <CheckDepositEndorsementSheet
                isOpen={isEndorsementSheetOpen}
                accountNumberMasked={endorsementAccountNumberMasked}
                onClose={() => {
                    setIsEndorsementSheetOpen(false);
                    setPendingDetails(null);
                }}
                onContinue={handleEndorsementContinue}
            />
        </>
    );
};

export default CheckDeposit;
